import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Card, ListGroup, Badge, Alert, Spinner } from "react-bootstrap";
import { FaUsers, FaSearch, FaTrash, FaCheck, FaExclamationTriangle, FaCamera, FaChartBar, FaTimes, FaTasks } from "react-icons/fa";
import { getClassesBySchoolId, getStudentsBySchoolId, deleteStudent, batchDeleteStudents, getIndividualStudentResults } from "../services/APIClient";

function DeleteStudentsModal({ show, onHide, schoolid, teacherid, teacherName }) {
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Track which students have had their results checked - now stores string status
  const [studentsWithResultsData, setStudentsWithResultsData] = useState(new Map());
  const [resultsLoadingStarted, setResultsLoadingStarted] = useState(false);

  // Filter states - now with new defaults
  const [studentGradeFilter, setStudentGradeFilter] = useState("all");
  const [studentAcademicYearFilter, setStudentAcademicYearFilter] = useState("all"); // Will be updated after loading
  const [studentSnapshotFilter, setStudentSnapshotFilter] = useState("active"); // Default to active classes
  const [studentResultsFilter, setStudentResultsFilter] = useState("all"); // Will set to "screener_assigned" after loading data
  const [studentSearchTerm, setStudentSearchTerm] = useState("");
  const [deletionReason, setDeletionReason] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Available grade levels using boolean system
  const gradeLevels = [
    { value: "all", label: "All Grades" },
    { value: "grade_level1", label: "Junior Infants" },
    { value: "grade_level2", label: "Senior Infants" },
    { value: "grade_level3", label: "1st Class" },
    { value: "grade_level4", label: "2nd Class" },
    { value: "grade_level5", label: "3rd Class" },
    { value: "grade_level6", label: "4th Class" },
    { value: "grade_level7", label: "5th Class" },
    { value: "grade_level8", label: "6th Class" }
  ];

  // Academic years - will be populated from actual data
  const [availableAcademicYears, setAvailableAcademicYears] = useState([
    { value: "all", label: "All Academic Years" }
  ]);

  // Snapshot filter options
  const snapshotOptions = [
    { value: "all", label: "All Students" },
    { value: "active", label: "In Active Classes Only" },
    { value: "snapshot", label: "In Snapshot Classes Only" }
  ];

  // Results filter options - NOW WITH 3 STATES
  const resultsOptions = [
    { value: "all", label: "All Students" },
    { value: "has_results", label: "Has Results" },
    { value: "screener_assigned", label: "Screener Assigned - No Results" },
    { value: "no_results", label: "No Screeners Assigned" }
  ];

  const getGradeLabelFromClass = (classItem) => {
    if (!classItem) return "Unknown Grade";
    if (classItem.grade_level1) return "Junior Infants";
    if (classItem.grade_level2) return "Senior Infants";
    if (classItem.grade_level3) return "1st Class";
    if (classItem.grade_level4) return "2nd Class";
    if (classItem.grade_level5) return "3rd Class";
    if (classItem.grade_level6) return "4th Class";
    if (classItem.grade_level7) return "5th Class";
    if (classItem.grade_level8) return "6th Class";
    return "Unknown Grade";
  };

  const getGradeBooleanFromClass = (classItem) => {
    if (!classItem) return "unknown";
    if (classItem.grade_level1) return "grade_level1";
    if (classItem.grade_level2) return "grade_level2";
    if (classItem.grade_level3) return "grade_level3";
    if (classItem.grade_level4) return "grade_level4";
    if (classItem.grade_level5) return "grade_level5";
    if (classItem.grade_level6) return "grade_level6";
    if (classItem.grade_level7) return "grade_level7";
    if (classItem.grade_level8) return "grade_level8";
    return "unknown";
  };

  // UPDATED: Function to check if a student has results - NOW RETURNS 3 STATES
  const checkStudentResults = async (studentId) => {
    try {
      const results = await getIndividualStudentResults(studentId);

      if (!results || results.length === 0) {
        return 'no_screeners';  // No screeners assigned at all
      }

      // Check if any assessment has actual completed tasks
      const hasCompletedTasks = results.some(r => r.tasks && r.tasks.length > 0);

      if (hasCompletedTasks) {
        return 'has_results';  // Has completed tasks
      } else {
        return 'screener_assigned';  // Has screeners but no completed tasks
      }
    } catch (error) {
      console.error(`Error checking results for student ${studentId}:`, error);
      return 'no_screeners';  // Default to safest assumption
    }
  };

  // Load results for a batch of students
  const loadResultsForStudents = async (studentIds) => {
    const newResultsMap = new Map(studentsWithResultsData);

    // Process in smaller batches to avoid overwhelming the API
    const batchSize = 10;
    for (let i = 0; i < studentIds.length; i += batchSize) {
      const batch = studentIds.slice(i, i + batchSize);

      const batchPromises = batch.map(async (studentId) => {
        if (!newResultsMap.has(studentId)) {
          const resultStatus = await checkStudentResults(studentId);
          newResultsMap.set(studentId, resultStatus);  // Store 'has_results', 'screener_assigned', or 'no_screeners'
        }
        return studentId;
      });

      await Promise.all(batchPromises);

      // Update state after each batch so user sees progress
      setStudentsWithResultsData(new Map(newResultsMap));

      // Small delay between batches
      if (i + batchSize < studentIds.length) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    return newResultsMap;
  };

  // Handle results filter change - only load results when needed
  const handleResultsFilterChange = async (filterValue) => {
    setStudentResultsFilter(filterValue);

    // Only load results if user selected one of the specific filters
    if ((filterValue === "has_results" || filterValue === "screener_assigned" || filterValue === "no_results")
      && !resultsLoadingStarted) {
      setLoadingResults(true);
      setResultsLoadingStarted(true);

      try {
        // Get currently filtered students (excluding results filter)
        const currentlyFilteredStudents = students.filter(student => {
          const matchesGrade = studentGradeFilter === "all" || student.gradeBoolean === studentGradeFilter;
          const matchesAcademicYear = studentAcademicYearFilter === "all" || student.academicYear === studentAcademicYearFilter;

          let matchesSnapshot = true;
          if (studentSnapshotFilter === "active") {
            matchesSnapshot = !student.isSnapshot;
          } else if (studentSnapshotFilter === "snapshot") {
            matchesSnapshot = student.isSnapshot;
          }

          const matchesSearch = student.name?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
            student.firstName?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
            student.lastName?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
            student.className?.toLowerCase().includes(studentSearchTerm.toLowerCase());

          return matchesGrade && matchesAcademicYear && matchesSnapshot && matchesSearch;
        });

        const studentIds = currentlyFilteredStudents.map(s => s.id);
        await loadResultsForStudents(studentIds);

      } catch (error) {
        console.error("Error loading results:", error);
        setError("Failed to load results data. Please try again.");
      } finally {
        setLoadingResults(false);
      }
    }
  };

  // After initial modal show, load students
  useEffect(() => {
    if (show && schoolid) {
      setInitialLoadComplete(false);
      loadStudents();
      // Reset results data when modal opens
      setStudentsWithResultsData(new Map());
      setResultsLoadingStarted(false);
    }
  }, [show, schoolid]);

  const loadStudents = async () => {
    setLoading(true);
    setError("");
    try {
      const studentsResponse = await getStudentsBySchoolId(schoolid);

      console.log("Students response:", studentsResponse);

      // Transform student data - NO results checking here
      const transformedStudents = (studentsResponse || []).map(student => {
        const gradeLabel = student.class ? getGradeLabelFromClass(student.class) : 'Unknown Grade';
        const gradeBoolean = student.class ? getGradeBooleanFromClass(student.class) : 'unknown';

        return {
          id: student.id,
          name: student.name,
          firstName: student.firstName,
          lastName: student.lastName,
          classId: student.classId,
          className: student.class?.name || 'Unknown Class',
          grade: gradeLabel,
          gradeBoolean: gradeBoolean,
          academicYear: student.class?.academicYear || null,
          isSnapshot: student.class?.snapshot || false
        };
      }).sort((a, b) => {
        const nameA = (a.firstName && a.lastName) ? `${a.firstName} ${a.lastName}` : a.name || '';
        const nameB = (b.firstName && b.lastName) ? `${b.firstName} ${b.lastName}` : b.name || '';
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
      });

      // Extract unique academic years
      const uniqueAcademicYears = [...new Set(transformedStudents
        .map(student => student.academicYear)
        .filter(year => year)
      )].sort().reverse();

      const academicYearOptions = [
        { value: "all", label: "All Academic Years" },
        ...uniqueAcademicYears.map(year => ({
          value: year,
          label: `${year} Academic Year`
        }))
      ];

      setAvailableAcademicYears(academicYearOptions);
      setStudents(transformedStudents);

      // Set default academic year to the most recent (assumed to be first after sorting in reverse)
      if (uniqueAcademicYears.length > 0) {
        // Check if "25-26" academic year exists
        const targetYear = "25-26";
        const targetYearExists = uniqueAcademicYears.includes(targetYear);

        if (targetYearExists) {
          setStudentAcademicYearFilter(targetYear);
        } else {
          // Default to the most recent year
          setStudentAcademicYearFilter(uniqueAcademicYears[0]);
        }
      }

      // Now that we have data, set the default results filter to "screener_assigned"
      // This will trigger the API call for checking results
      setInitialLoadComplete(true);

    } catch (err) {
      setError("Failed to load students. Please try again.");
      console.error("Error loading students:", err);
    } finally {
      setLoading(false);
    }
  };

  // Set default "screener_assigned" filter after initial data load is complete
  useEffect(() => {
    if (initialLoadComplete && !resultsLoadingStarted) {
      handleResultsFilterChange("screener_assigned");
    }
  }, [initialLoadComplete]);

  const handleStudentSelect = (student) => {
    setSelectedStudents(prev => {
      const isSelected = prev.find(s => s.id === student.id);
      if (isSelected) {
        return prev.filter(s => s.id !== student.id);
      } else {
        return [...prev, student];
      }
    });
  };

  const handleSelectAllStudents = () => {
    const filteredStudents = getFilteredStudents();
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents);
    }
  };

  const getFilteredStudents = () => {
    return students.filter(student => {
      const matchesGrade = studentGradeFilter === "all" || student.gradeBoolean === studentGradeFilter;
      const matchesAcademicYear = studentAcademicYearFilter === "all" || student.academicYear === studentAcademicYearFilter;

      // Snapshot filtering logic
      let matchesSnapshot = true;
      if (studentSnapshotFilter === "active") {
        matchesSnapshot = !student.isSnapshot;
      } else if (studentSnapshotFilter === "snapshot") {
        matchesSnapshot = student.isSnapshot;
      }

      // UPDATED: Results filtering logic with 3 states
      let matchesResults = true;
      if (studentResultsFilter !== "all") {
        const resultStatus = studentsWithResultsData.get(student.id);

        if (studentResultsFilter === "has_results") {
          matchesResults = resultStatus === 'has_results';
        } else if (studentResultsFilter === "screener_assigned") {
          matchesResults = resultStatus === 'screener_assigned';
        } else if (studentResultsFilter === "no_results") {
          matchesResults = resultStatus === 'no_screeners';
        }
      }

      const matchesSearch = student.name?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
        student.firstName?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
        student.lastName?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
        student.className?.toLowerCase().includes(studentSearchTerm.toLowerCase());

      return matchesGrade && matchesAcademicYear && matchesSnapshot && matchesResults && matchesSearch;
    });
  };

  const handleDeleteClick = () => {
    if (selectedStudents.length === 0) {
      setError("Please select at least one student to delete.");
      return;
    }
    if (!deletionReason.trim()) {
      setError("Please provide a reason for deletion.");
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    setShowConfirmModal(false);
    await handleBulkDelete();
  };

  const handleBulkDelete = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await batchDeleteStudents({
        studentIds: selectedStudents.map(s => s.id),
        deletionReason: deletionReason.trim(),
        deletedByTeacherId: teacherid?.toString() || "unknown",
        deletedByTeacherName: teacherName || "Unknown Teacher"
      });

      const { deletedCount, deletedStudentIds, errors } = response;

      if (deletedCount > 0) {
        setSuccess(`Successfully deleted ${deletedCount} student(s).`);

        if (errors && errors.length > 0) {
          setError(`Warning: ${errors.length} student(s) could not be deleted. ${errors.map(e => e.error).join(', ')}`);
        }
      } else {
        setError("No students were deleted. Please try again.");
      }

      setSelectedStudents([]);
      setDeletionReason("");

      await loadStudents();

    } catch (err) {
      setError("Failed to delete students. Please try again.");
      console.error("Delete error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedStudents([]);
    setError("");
    setSuccess("");
    setStudentGradeFilter("all");
    setStudentAcademicYearFilter("all");
    setStudentSnapshotFilter("active"); // Reset to active classes
    setStudentResultsFilter("all");
    setStudentSearchTerm("");
    setDeletionReason("");
    setShowConfirmModal(false);
    setStudentsWithResultsData(new Map());
    setResultsLoadingStarted(false);
    setInitialLoadComplete(false);
    onHide();
  };

  const autoClearMessage = () => {
    setTimeout(() => {
      setError("");
      setSuccess("");
    }, 5000);
  };

  useEffect(() => {
    if (error || success) {
      autoClearMessage();
    }
  }, [error, success]);

  const filteredStudents = getFilteredStudents();

  // UPDATED: Get results status badge component with 3 states
  const getResultsBadge = (student) => {
    const resultStatus = studentsWithResultsData.get(student.id);

    // Only show results badges if the results filter is active or we have data for this student
    if (studentResultsFilter === "all" && resultStatus === undefined) {
      return null; // Don't show any badge if we haven't checked and filter is "all"
    }

    // Show loading badge while checking
    if (resultStatus === undefined && studentResultsFilter !== "all") {
      return (
        <Badge bg="secondary" className="me-2">
          <Spinner size="sm" className="me-1" />
          Checking...
        </Badge>
      );
    }

    // Show badge based on status
    if (resultStatus === 'has_results') {
      return (
        <Badge bg="success" className="me-2">
          <FaChartBar className="me-1" size={10} />
          Has Results
        </Badge>
      );
    } else if (resultStatus === 'screener_assigned') {
      return (
        <Badge bg="warning" className="me-2 text-dark">
          <FaTasks className="me-1" size={10} />
          Screener Assigned (No Results)
        </Badge>
      );
    } else if (resultStatus === 'no_screeners') {
      return (
        <Badge bg="outline-secondary" className="me-2 text-muted">
          <FaTimes className="me-1" size={10} />
          No Screeners
        </Badge>
      );
    }

    return null;
  };

  // Calculate how many students still need results checking
  const getResultsLoadingProgress = () => {
    if (!loadingResults) return null;

    const studentsNeedingCheck = students.filter(student => {
      // Only count students that match current filters (excluding results filter)
      const matchesGrade = studentGradeFilter === "all" || student.gradeBoolean === studentGradeFilter;
      const matchesAcademicYear = studentAcademicYearFilter === "all" || student.academicYear === studentAcademicYearFilter;

      let matchesSnapshot = true;
      if (studentSnapshotFilter === "active") {
        matchesSnapshot = !student.isSnapshot;
      } else if (studentSnapshotFilter === "snapshot") {
        matchesSnapshot = student.isSnapshot;
      }

      const matchesSearch = student.name?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
        student.firstName?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
        student.lastName?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
        student.className?.toLowerCase().includes(studentSearchTerm.toLowerCase());

      return matchesGrade && matchesAcademicYear && matchesSnapshot && matchesSearch;
    });

    const totalToCheck = studentsNeedingCheck.length;
    const checkedCount = studentsNeedingCheck.filter(s => studentsWithResultsData.has(s.id)).length;

    return `${checkedCount}/${totalToCheck}`;
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTrash className="me-2 text-danger" />
            Delete Students
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ minHeight: '600px' }}>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              Select Students to Delete
              <Badge bg="danger" className="ms-2">{selectedStudents.length} selected</Badge>
              {loadingResults && (
                <Badge bg="info" className="ms-2">
                  <Spinner size="sm" className="me-1" />
                  Loading Results {getResultsLoadingProgress()}
                </Badge>
              )}
            </h5>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleSelectAllStudents}
              disabled={filteredStudents.length === 0}
            >
              {selectedStudents.length === filteredStudents.length ? "Deselect All" : "Select All"}
            </Button>
          </div>

          {/* Results Filter Notice */}
          {studentResultsFilter !== "all" && !resultsLoadingStarted && (
            <Alert variant="info" className="mb-3">
              <FaChartBar className="me-2" />
              Results data will be loaded for filtered students. This may take a moment for large student lists.
            </Alert>
          )}

          {/* Student Filters */}
          <Row className="mb-3">
            <Col md={2}>
              <Form.Select
                value={studentAcademicYearFilter}
                onChange={(e) => setStudentAcademicYearFilter(e.target.value)}
                size="sm"
              >
                {availableAcademicYears.map(year => (
                  <option key={year.value} value={year.value}>
                    {year.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={studentGradeFilter}
                onChange={(e) => setStudentGradeFilter(e.target.value)}
                size="sm"
              >
                {gradeLevels.map(grade => (
                  <option key={grade.value} value={grade.value}>
                    {grade.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={studentSnapshotFilter}
                onChange={(e) => setStudentSnapshotFilter(e.target.value)}
                size="sm"
              >
                {snapshotOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={studentResultsFilter}
                onChange={(e) => handleResultsFilterChange(e.target.value)}
                size="sm"
              >
                {resultsOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <div className="position-relative">
                <Form.Control
                  type="text"
                  placeholder="Search students, classes..."
                  value={studentSearchTerm}
                  onChange={(e) => setStudentSearchTerm(e.target.value)}
                  size="sm"
                />
                <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
              </div>
            </Col>
          </Row>

          {/* Deletion Reason */}
          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label className="fw-medium">
                  Reason for Deletion <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Please provide a reason for deleting these students (required for audit purposes)..."
                  value={deletionReason}
                  onChange={(e) => setDeletionReason(e.target.value)}
                  className={!deletionReason.trim() && selectedStudents.length > 0 ? 'border-warning' : ''}
                />
                <Form.Text className="text-muted">
                  This reason will be logged for audit purposes and cannot be changed after deletion.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Students List */}
          <div style={{ height: '350px', overflowY: 'auto' }}>
            {loading ? (
              <div className="text-center py-4">Loading students...</div>
            ) : filteredStudents.length === 0 ? (
              <div className="text-center py-4 text-muted">
                {studentResultsFilter !== "all" && loadingResults ?
                  "Loading results data..." :
                  "No students found"
                }
              </div>
            ) : (
              <ListGroup>
                {filteredStudents.map(student => (
                  <ListGroup.Item
                    key={student.id}
                    className={`d-flex justify-content-between align-items-center cursor-pointer ${selectedStudents.find(s => s.id === student.id) ? 'bg-danger-light border-danger' : ''
                      }`}
                    onClick={() => handleStudentSelect(student)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center">
                        <div className="fw-medium me-2">
                          {student.firstName && student.lastName
                            ? `${student.firstName} ${student.lastName}`
                            : student.name}
                        </div>
                        {getResultsBadge(student)}
                        {student.isSnapshot && (
                          <Badge bg="secondary" className="me-2">
                            <FaCamera className="me-1" size={10} />
                            Snapshot Class
                          </Badge>
                        )}
                      </div>
                      <small className="text-muted">
                        Class: {student.className} ({student.grade})
                        {student.academicYear && ` • ${student.academicYear}`}
                      </small>
                    </div>
                    {selectedStudents.find(s => s.id === student.id) && (
                      <FaCheck className="text-danger" />
                    )}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-between w-100 align-items-center">
            <div className="text-muted">
              {selectedStudents.length > 0 && (
                <span>
                  <FaTrash className="me-1 text-danger" />
                  {selectedStudents.length} student(s) selected for deletion
                  {selectedStudents.some(s => s.isSnapshot) && (
                    <Badge bg="warning" className="ms-2 text-dark">
                      <FaCamera className="me-1" size={10} />
                      Includes Snapshot Students
                    </Badge>
                  )}
                </span>
              )}
            </div>
            <div>
              <Button variant="secondary" onClick={handleClose} className="me-2">
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteClick}
                disabled={selectedStudents.length === 0 || !deletionReason.trim() || loading}
              >
                {loading ? "Deleting..." : `Delete ${selectedStudents.length > 0 ? selectedStudents.length : ''} Student${selectedStudents.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal - UPDATED with 3-state warnings */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title className="d-flex align-items-center">
            <FaExclamationTriangle className="me-2" />
            Confirm Student Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="text-center mb-4">
            <div className="bg-danger-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
              style={{ width: '60px', height: '60px' }}>
              <FaTrash className="text-danger" size={24} />
            </div>
            <h5 className="mb-2 text-danger">Delete {selectedStudents.length} Student{selectedStudents.length !== 1 ? 's' : ''}</h5>
            <p className="text-muted mb-0">This action cannot be undone. Please review carefully.</p>
          </div>

          {/* Students List */}
          <div className="mb-4">
            <h6 className="mb-3 d-flex align-items-center">
              <Badge bg="danger" className="me-2">{selectedStudents.length}</Badge>
              Students to be deleted
            </h6>
            <div className="border rounded-3 bg-light p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {selectedStudents.map((student, index) => (
                <div key={student.id} className="d-flex justify-content-between align-items-center py-2 px-2 mb-2 bg-white rounded border-start border-danger border-3"
                  style={{ borderStartWidth: '4px !important' }}>
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center">
                      <div className="fw-medium text-dark me-2">
                        {student.firstName && student.lastName
                          ? `${student.firstName} ${student.lastName}`
                          : student.name}
                      </div>
                      {studentsWithResultsData.get(student.id) === 'has_results' && (
                        <Badge bg="success" className="me-2">
                          <FaChartBar className="me-1" size={10} />
                          Has Results
                        </Badge>
                      )}
                      {studentsWithResultsData.get(student.id) === 'screener_assigned' && (
                        <Badge bg="warning" className="me-2 text-dark">
                          <FaTasks className="me-1" size={10} />
                          Screener Assigned (No Results)
                        </Badge>
                      )}
                      {student.isSnapshot && (
                        <Badge bg="secondary">
                          <FaCamera className="me-1" size={10} />
                          Snapshot
                        </Badge>
                      )}
                    </div>
                    <small className="text-muted">
                      Class: {student.className}
                      {student.academicYear && ` • ${student.academicYear}`}
                    </small>
                  </div>
                  <Badge bg="outline-secondary" className="text-muted">{student.grade}</Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Deletion Details */}
          <div className="rounded-3 p-4 mb-4"
            style={{ background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)' }}>
            <div className="row">
              <div className="col-12">
                <div className="mb-3">
                  <strong>Deletion Reason:</strong>
                  <div className="mt-1 p-2 bg-white rounded border">
                    {deletionReason || "No reason provided"}
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <small className="text-muted">Deleted by:</small>
                    <div className="fw-medium">{teacherName || "Unknown Teacher"}</div>
                  </div>
                  <div className="col-6">
                    <small className="text-muted">Deletion date:</small>
                    <div className="fw-medium">{new Date().toLocaleDateString()}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* UPDATED: Warning with 3 states */}
          <div className={`alert ${selectedStudents.some(s => s.isSnapshot) ? 'alert-warning' : 'alert-danger'} d-flex align-items-start`} role="alert">
            <div className="me-2">⚠️</div>
            <div>
              <strong>CRITICAL WARNING:</strong> This action will permanently delete all selected students from the system.
              This includes all their data, assessments, and progress records. This action cannot be undone.
              {selectedStudents.some(s => s.isSnapshot) && (
                <div className="mt-2">
                  <strong>Note:</strong> Some students are in snapshot classes. Deleting students from snapshot classes may affect historical records.
                </div>
              )}
              {selectedStudents.some(s => studentsWithResultsData.get(s.id) === 'has_results') && (
                <div className="mt-2">
                  <strong>Assessment Data Warning:</strong> Some students have completed assessment results that will be permanently deleted.
                </div>
              )}
              {selectedStudents.some(s => studentsWithResultsData.get(s.id) === 'screener_assigned') && (
                <div className="mt-2">
                  <strong>Assigned Screeners:</strong> Some students have screeners assigned but not completed. These assignments will be deleted.
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light border-0 p-4">
          <div className="d-flex w-100 justify-content-between align-items-center">
            <div className="text-muted">
              <small>
                <strong>{selectedStudents.length}</strong> student{selectedStudents.length !== 1 ? 's' : ''} will be permanently deleted
              </small>
            </div>
            <div>
              <Button variant="outline-secondary" onClick={() => setShowConfirmModal(false)} className="me-3">
                Cancel
              </Button>
              <Button variant="danger" onClick={handleConfirmDelete} size="lg" className="px-4">
                Confirm Deletion
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default DeleteStudentsModal;